import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Megaphone, Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  Link2, Eraser, Pin, PinOff, Pencil, Trash2, Loader2, Plus, X, Save,
} from 'lucide-react';

const NOTICE_HTML_CLS = 'text-sm text-slate-700 leading-relaxed break-words ' +
  '[&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:min-h-[1em]';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const ToolBtn = ({ icon: Icon, label, onClick, testId }) => (
  <button type="button" title={label} data-testid={testId}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
    <Icon className="w-4 h-4" />
  </button>
);

const RichEditor = ({ initialHtml, editorRef }) => {
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };
  const addLink = () => {
    const url = window.prompt('Cole o link (ex.: https://chat.whatsapp.com/...):');
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (sel && sel.toString()) {
      document.execCommand('createLink', false, href);
    } else {
      document.execCommand('insertHTML', false,
        `<a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>&nbsp;`);
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50/80 px-2 py-1.5">
        <ToolBtn icon={Bold} label="Negrito" onClick={() => exec('bold')} testId="notice-tool-bold" />
        <ToolBtn icon={Italic} label="Itálico" onClick={() => exec('italic')} testId="notice-tool-italic" />
        <ToolBtn icon={Underline} label="Sublinhado" onClick={() => exec('underline')} testId="notice-tool-underline" />
        <ToolBtn icon={Strikethrough} label="Tachado" onClick={() => exec('strikeThrough')} testId="notice-tool-strike" />
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolBtn icon={List} label="Lista" onClick={() => exec('insertUnorderedList')} testId="notice-tool-ul" />
        <ToolBtn icon={ListOrdered} label="Lista numerada" onClick={() => exec('insertOrderedList')} testId="notice-tool-ol" />
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolBtn icon={Link2} label="Inserir link" onClick={addLink} testId="notice-tool-link" />
        <ToolBtn icon={Eraser} label="Limpar formatação" onClick={() => exec('removeFormat')} testId="notice-tool-clear" />
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning
        data-testid="notice-editor"
        className={`min-h-[140px] max-h-80 overflow-y-auto px-3 py-2.5 outline-none focus:bg-blue-50/20 ${NOTICE_HTML_CLS}`}
        dangerouslySetInnerHTML={{ __html: initialHtml || '' }} />
    </div>
  );
};

export default function HostNoticesManagement() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [pinned, setPinned] = useState(false);
  const [initialHtml, setInitialHtml] = useState('');
  const editorRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/agency/notices');
      setNotices(res.data.notices || []);
    } catch {
      toast.error('Erro ao carregar avisos.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null); setTitle(''); setPinned(false); setInitialHtml('');
    setFormOpen(true);
  };
  const openEdit = (n) => {
    setEditingId(n.id); setTitle(n.title); setPinned(!!n.pinned);
    setInitialHtml(n.content_html || '');
    setFormOpen(true);
  };
  const closeForm = () => { setFormOpen(false); setEditingId(null); };

  const save = async () => {
    const content_html = editorRef.current?.innerHTML || '';
    const plain = editorRef.current?.innerText?.trim() || '';
    if (!title.trim()) { toast.error('Informe o título do aviso.'); return; }
    if (!plain && !content_html.includes('<a')) { toast.error('Escreva o conteúdo do aviso.'); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/agency/notices/${editingId}`, { title, content_html, pinned });
        toast.success('Aviso atualizado!');
      } else {
        await api.post('/agency/notices', { title, content_html, pinned });
        toast.success('Aviso publicado para os hosts!');
      }
      closeForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar aviso.');
    }
    setBusy(false);
  };

  const togglePin = async (n) => {
    try {
      await api.patch(`/agency/notices/${n.id}`, { pinned: !n.pinned });
      load();
    } catch { toast.error('Erro ao fixar aviso.'); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Excluir o aviso "${n.title}"?`)) return;
    try {
      await api.delete(`/agency/notices/${n.id}`);
      toast.success('Aviso excluído.');
      load();
    } catch { toast.error('Erro ao excluir aviso.'); }
  };

  return (
    <Card data-testid="host-notices-admin">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-orange-500" /> Avisos para os Hosts
          </CardTitle>
          {!formOpen && (
            <Button size="sm" onClick={openNew} data-testid="notice-new-button"
              className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1" /> Novo aviso
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Os avisos aparecem na aba <b>Avisos</b> do Painel do Host. Use a barra para formatar o texto e inserir links de grupos.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {formOpen && (
          <div className="rounded-2xl border border-orange-200 bg-orange-50/40 p-3 sm:p-4 space-y-3" data-testid="notice-form">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150}
              placeholder="Título do aviso (ex.: Grupo oficial dos hosts no WhatsApp)"
              data-testid="notice-title-input" />
            <RichEditor initialHtml={initialHtml} editorRef={editorRef} />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button type="button" onClick={() => setPinned(!pinned)} data-testid="notice-pinned-toggle"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${pinned
                  ? 'border-amber-300 bg-amber-100 text-amber-700'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
                <Pin className="w-3.5 h-3.5" /> {pinned ? 'Fixado no topo' : 'Fixar no topo'}
              </button>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={closeForm} data-testid="notice-cancel-button">
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button size="sm" onClick={save} disabled={busy} data-testid="notice-save-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  {editingId ? 'Salvar alterações' : 'Publicar aviso'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-orange-500 animate-spin" /></div>
        ) : notices.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8" data-testid="notices-empty">
            Nenhum aviso publicado ainda. Clique em “Novo aviso” para criar o primeiro.
          </p>
        ) : (
          <div className="space-y-3" data-testid="notices-list">
            {notices.map((n) => (
              <div key={n.id} data-testid={`notice-item-${n.id}`}
                className={`rounded-xl border p-3 sm:p-4 ${n.pinned ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-white'}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                      {n.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                      {n.title}
                    </p>
                    <p className="text-[11px] text-slate-400">Publicado em {fmtDate(n.created_at)}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => togglePin(n)}
                      data-testid={`notice-pin-${n.id}`} title={n.pinned ? 'Desafixar' : 'Fixar no topo'}
                      className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50">
                      {n.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(n)}
                      data-testid={`notice-edit-${n.id}`}
                      className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(n)}
                      data-testid={`notice-delete-${n.id}`}
                      className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className={`mt-2 ${NOTICE_HTML_CLS}`}
                  dangerouslySetInnerHTML={{ __html: n.content_html }} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
